
import { MatchWeatherBadge } from './MatchWeatherBadge';
import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClickableAvatar } from '@/components/ui/clickable-avatar';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Calendar, MapPin, Clock, Trophy, Timer, CalendarPlus, Trash2, History, MessageCircle, UserCog } from 'lucide-react';
import { useGroupAdmin } from '@/hooks/useGroupAdmin';
import { ReplacePlayerDialog } from './ReplacePlayerDialog';
import type { MatchPlayerSlot } from '@/hooks/useReplaceScheduledMatchPlayer';
import { ScheduledMatch } from '@/types/scheduledMatch';
import { format, addHours, addMinutes, differenceInMinutes, isBefore } from 'date-fns';
import { getInitials } from '@/utils/stringUtils';
import { getTimezoneAbbr, parseDateTimeString } from '@/utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useScheduledMatches } from '@/hooks/useScheduledMatches';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { getCalendarButtonText } from '@/utils/calendarUtils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import PartnershipHistoryDialog from '@/components/PartnershipHistoryDialog';
import { useTranslation } from 'react-i18next';
import { PlaytomicBookingButton } from '@/components/booking/PlaytomicBookingButton';
import { MatchWinProbability } from '@/components/scheduled/MatchWinProbability';
import { useMatchBookingStatus } from '@/hooks/useMatchBookingStatus';
import { CheckCircle } from 'lucide-react';
import { notifyBookingInitiated } from '@/utils/bookingNotifications';
import { buildRosterLabel } from '@/utils/bookingNotifications';

interface ScheduledMatchCardProps {
  match: ScheduledMatch;
  highlightMatchId?: string;
}

export const ScheduledMatchCard: React.FC<ScheduledMatchCardProps> = ({
  match,
  highlightMatchId
}) => {
  const navigate = useNavigate();
  const { generateCalendarFile, deleteScheduledMatch } = useScheduledMatches();
  const { prefs } = useNotificationPreferences();
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [partnershipDialogOpen, setPartnershipDialogOpen] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [courtDuration, setCourtDuration] = useState<number>(90);
  const [slotSheetOpen, setSlotSheetOpen] = useState(false);
  const [replaceSlot, setReplaceSlot] = useState<MatchPlayerSlot | null>(null);
  const { data: bookingStatus } = useMatchBookingStatus(match.id);
  const { isAdmin: isGroupAdmin } = useGroupAdmin(match.group_id);
  const queryClient = useQueryClient();
  const cardRef = useRef<HTMLDivElement>(null);

  const isTarget = !!highlightMatchId && match.id === highlightMatchId;

  useEffect(() => {
    if (!isTarget) return;
    setIsHighlighted(true);
    const scrollTimer = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    const highlightTimer = setTimeout(() => setIsHighlighted(false), 3500);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(highlightTimer);
    };
  }, [isTarget]);
  
  // Parse match_date — always treat as UTC (all match dates are stored as UTC via .toISOString())
  const matchDate = parseDateTimeString(match.match_date);
  const now = new Date();
  const expiresAt = match.expires_at ? new Date(match.expires_at) : addHours(matchDate, 36);
  // Matches are booked in 90-minute court slots — the Live window covers the court time only
  const MATCH_DURATION_MINUTES = 90;
  const matchEndDate = addMinutes(matchDate, MATCH_DURATION_MINUTES);

  const isMatchParticipant = !!user && [
    match.team1_player1,
    match.team1_player2,
    match.team2_player1,
    match.team2_player2,
  ].includes(user.id);

  // Lifecycle: Upcoming → Live (court time) → Awaiting Scores (until capture window closes) → Expired
  const isUpcoming = isBefore(now, matchDate);
  const isLive = !isUpcoming && isBefore(now, matchEndDate);
  const isAwaitingScores = !isUpcoming && !isLive && isBefore(now, expiresAt);
  const canCaptureScores = (isLive || isAwaitingScores) && isMatchParticipant;

  const remainingMinutes = Math.max(0, differenceInMinutes(expiresAt, now));
  const remainingHours = Math.floor(remainingMinutes / 60);
  const remainingMinsPart = remainingMinutes % 60;

  const urgency =
    remainingHours <= 1 ? 'high' :
    remainingHours <= 6 ? 'medium' : 'low';

  const getStatusBadge = () => {
    if (isLive) {
      return (
        <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px] px-2 py-0.5">
          <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          Live
        </Badge>
      );
    }
    if (isAwaitingScores) {
      return (
        <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px] px-2 py-0.5">
          Awaiting Scores
        </Badge>
      );
    }
    if (isUpcoming) {
      return (
        <Badge variant="outline" className="text-[10px] px-2 py-0.5 border-border/50">
          Scheduled
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
        Expired
      </Badge>
    );
  };

  const handleCaptureScores = () => {
    navigate(ROUTES.CAPTURE_SCHEDULED_MATCH_SCORES(match.id));
  };

  const handleAddToCalendar = () => {
    generateCalendarFile(match);
  };

  const handleDeleteMatch = async () => {
    if (!user || match.published_by !== user.id) return;
    
    setIsDeleting(true);
    try {
      const result = await deleteScheduledMatch(match.id) as any;
      if (result?.challengeCancelled) {
        toast({
          title: "Match Deleted & Challenge Cancelled",
          description: "The scheduled match was deleted. The linked challenge has been automatically cancelled and all players notified.",
        });
      } else {
        toast({
          title: "Match Deleted",
          description: "The scheduled match has been deleted.",
        });
      }
    } catch (error) {
      console.error('Error deleting match:', error);
      toast({
        title: "Error",
        description: "Failed to delete the match.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const calendarPreference = (prefs as any)?.calendar_integration || 'ask';
  const showCalendarButton = calendarPreference !== 'disabled';

  // Find current user's partner in this match
  const getUserPartner = () => {
    if (!user) return null;
    
    // Check if user is in team 1
    if (match.team1_player1 === user.id) {
      return match.team1_player2_profile;
    }
    if (match.team1_player2 === user.id) {
      return match.team1_player1_profile;
    }
    // Check if user is in team 2
    if (match.team2_player1 === user.id) {
      return match.team2_player2_profile;
    }
    if (match.team2_player2 === user.id) {
      return match.team2_player1_profile;
    }
    return null;
  };

  const userPartner = getUserPartner();

  const handleViewPartnerHistory = () => {
    if (userPartner?.id) {
      setSelectedPartnerId(userPartner.id);
      setPartnershipDialogOpen(true);
    }
  };

  const canReplacePlayers =
    !!user &&
    isUpcoming &&
    !match.challenge_match_id &&
    (isGroupAdmin || match.published_by === user.id);

  const slotForProfile = (profileId?: string): MatchPlayerSlot | null => {
    if (!profileId) return null;
    if (match.team1_player1 === profileId) return 'team1_player1';
    if (match.team1_player2 === profileId) return 'team1_player2';
    if (match.team2_player1 === profileId) return 'team2_player1';
    if (match.team2_player2 === profileId) return 'team2_player2';
    return null;
  };

  const openReplace = (slot: MatchPlayerSlot) => {
    setReplaceSlot(slot);
  };

  const PlayerAvatar = ({ profile, position, isPartner = false }: {
    profile?: { id: string; full_name: string; avatar_url?: string };
    position: 'top' | 'bottom';
    isPartner?: boolean;
  }) => {
    const name = profile?.full_name || 'Unknown';
    const firstName = name.split(' ')[0];
    const slot = slotForProfile(profile?.id);

    return (
      <div
        className={`flex items-center gap-2 min-w-0 ${isPartner ? 'cursor-pointer hover:bg-accent/50 rounded-lg p-1.5 -m-1.5 transition-colors' : ''}`}
        onClick={isPartner ? handleViewPartnerHistory : undefined}
        title={isPartner ? 'View partnership history' : undefined}
      >
        {profile?.id ? (
          <div className="relative flex-shrink-0">
            <ClickableAvatar
              userId={profile.id}
              avatarUrl={profile.avatar_url}
              fullName={name}
              className="h-9 w-9 ring-2 ring-background shadow-sm"
              size="sm"
            />
            {isPartner && (
              <div
                className="absolute -bottom-1.5 -right-1.5 h-6 w-6 bg-primary rounded-full flex items-center justify-center shadow-md border-2 border-background"
                role="button"
                aria-label="View partnership history"
              >
                <History className="h-3.5 w-3.5 text-primary-foreground" />
              </div>
            )}
          </div>
        ) : (
          <Avatar className="h-9 w-9 flex-shrink-0 ring-2 ring-background shadow-sm">
            <AvatarImage src={profile?.avatar_url} />
            <AvatarFallback className="text-xs bg-muted">
              {profile?.full_name ? getInitials(profile.full_name) : '?'}
            </AvatarFallback>
          </Avatar>
        )}
        <span className="text-xs font-medium truncate flex-1 min-w-0">
          {firstName}
        </span>
        {canReplacePlayers && slot && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openReplace(slot);
            }}
            className="flex-shrink-0 h-7 w-7 flex items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            aria-label={`Replace ${firstName}`}
            title="Replace player"
          >
            <UserCog className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
    <div ref={cardRef}>
    <Card className={`relative overflow-hidden bg-card border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg ${isHighlighted ? 'ring-2 ring-primary shadow-lg shadow-primary/20' : ''}`}>
      {/* Header strip */}
      <div className="h-1 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />

      {match.club?.id && isBefore(now, addHours(matchDate, 2)) && (
        <div className="absolute top-2 right-2 z-10">
          <MatchWeatherBadge clubId={match.club.id} matchDate={matchDate} variant="icon" />
        </div>
      )}
      
      <div className="p-4 space-y-4">
        {/* Top row: Group name + Status */}
        <div className={`flex items-center justify-between gap-2 ${match.club?.id ? 'pr-7' : ''}`}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary text-xs font-bold">
                {(match.group_name || 'PM')[0]}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">
                {match.group_name || 'Private Match'}
              </p>
              {match.poll_title && (
                <p className="text-[10px] text-muted-foreground truncate">
                  {match.poll_title}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {getStatusBadge()}
          </div>
        </div>

        {/* Date/Time/Location row */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>{format(matchDate, 'MMM d')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            <span>{format(matchDate, 'HH:mm')} <span className="text-muted-foreground">{getTimezoneAbbr(matchDate)}</span></span>
          </div>
          {match.club && (
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{match.club.club_name}</span>
            </div>
          )}
        </div>

        {/* Status chip: in-progress during court time, countdown once the match has finished */}
        {isLive && (
          <div className="flex items-center justify-center gap-2 py-1.5 px-3 rounded-md text-xs font-medium bg-primary/10 text-primary">
            <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span>Match in progress — scores can be captured</span>
          </div>
        )}
        {isAwaitingScores && (
          <div className={`flex items-center justify-center gap-2 py-1.5 px-3 rounded-md text-xs font-medium ${
            urgency === 'high' 
              ? 'bg-destructive/10 text-destructive' 
              : urgency === 'medium' 
                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' 
                : 'bg-primary/10 text-primary'
          }`}>
            <Timer className="h-3.5 w-3.5" />
            <span>Capture ends in {remainingHours}h {remainingMinsPart}m</span>
          </div>
        )}

        {/* Teams display */}
        <div className="relative py-3">
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-2 items-center">
            {/* Team 1 */}
            <div className="space-y-2 min-w-0">
              <PlayerAvatar profile={match.team1_player1_profile} position="top" isPartner={userPartner?.id === match.team1_player1_profile?.id} />
              {match.team1_player2 && (
                <PlayerAvatar profile={match.team1_player2_profile} position="bottom" isPartner={userPartner?.id === match.team1_player2_profile?.id} />
              )}
            </div>

            {/* VS divider */}
            <div className="flex flex-col items-center justify-center px-1">
              <div className="w-px h-4 bg-border" />
              <span className="text-[10px] font-bold text-muted-foreground/60 my-1">VS</span>
              <div className="w-px h-4 bg-border" />
            </div>

            {/* Team 2 */}
            <div className="space-y-2 min-w-0">
              <PlayerAvatar profile={match.team2_player1_profile} position="top" isPartner={userPartner?.id === match.team2_player1_profile?.id} />
              {match.team2_player2 && (
                <PlayerAvatar profile={match.team2_player2_profile} position="bottom" isPartner={userPartner?.id === match.team2_player2_profile?.id} />
              )}
            </div>
          </div>
        </div>

        <MatchWinProbability
          team1={(match as any).win_probability_team1}
          team2={(match as any).win_probability_team2}
          breakdown={(match as any).win_probability_breakdown}
        />

        {/* Booking section — show booked badge, pending state, OR booking CTAs */}
        {isBefore(now, matchDate) && match.club && (() => {
          const bookingState = bookingStatus?.status || 'none';

          // Booked state — show confirmed badge with court info
          if (bookingState === 'booked') {
            return (
              <div className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-primary/10 border border-primary/20">
                <CheckCircle size={14} className="text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-primary">
                    Court booked by {bookingStatus?.bookedByName || 'a player'}
                  </span>
                  {bookingStatus?.courtName && (
                    <span className="text-[10px] text-muted-foreground block truncate">
                      Court: {bookingStatus.courtName}
                    </span>
                  )}
                </div>
              </div>
            );
          }

          // Pending state — someone initiated a booking
          if (bookingState === 'pending') {
            return (
              <div className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Clock size={14} className="text-amber-500 flex-shrink-0 animate-pulse" />
                <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                  Booking initiated by {bookingStatus?.bookedByName || 'a player'}
                </span>
              </div>
            );
          }

          // None state — show booking CTAs
          const hasWhatsApp = !!match.club.phone_number;
          const hasPlaytomic = !!(match.club.playtomic_tenant_id && match.club.slug);
          if (!hasWhatsApp && !hasPlaytomic) return null;

          const sanitized = match.club.phone_number?.replace(/[^\d]/g, '') || '';
          const matchType = (match.team1_player2 || match.team2_player2) ? 'Doubles (2v2)' : 'Singles (1v1)';
          const message = t('matches.whatsapp.messageCard', {
            club: match.club.club_name,
            date: format(matchDate, 'MMM d, yyyy'),
            time: format(matchDate, 'HH:mm'),
            matchType,
            duration: courtDuration,
          });
          const waUrl = sanitized ? `https://wa.me/${sanitized}?text=${encodeURIComponent(message)}` : '';

          const handleWhatsAppClick = async () => {
            // Insert pending row to lock the match (skip if already pending for this user)
            if (user) {
              try {
                const { data: existing } = await supabase
                  .from('booking_feedback')
                  .select('id')
                  .eq('match_id', match.id)
                  .eq('user_id', user.id)
                  .is('booked', null)
                  .maybeSingle();

                if (!existing) {
                  await supabase.from('booking_feedback').insert({
                    user_id: user.id,
                    match_id: match.id,
                    booking_channel: 'whatsapp',
                    club_name: match.club?.club_name || null,
                    booked: null,
                  });
                  queryClient.invalidateQueries({ queryKey: ['match-booking-status', match.id] });
                  const rosterLabel = buildRosterLabel(
                    match.team1_player1_profile?.full_name,
                    match.team1_player2_profile?.full_name,
                    match.team2_player1_profile?.full_name,
                    match.team2_player2_profile?.full_name
                  );
                  const allPlayerIds = [match.team1_player1, match.team1_player2, match.team2_player1, match.team2_player2].filter(Boolean) as string[];
                  notifyBookingInitiated(
                    {
                      matchId: match.id,
                      groupId: match.group_id,
                      playerIds: allPlayerIds,
                      currentUserId: user.id,
                      matchDate: match.match_date,
                      clubName: match.club?.club_name || undefined,
                      rosterLabel,
                    },
                    profile?.full_name || 'A player'
                  );
                }
              } catch (e) {
                console.error('Failed to insert pending booking feedback:', e);
              }
            }
            localStorage.setItem('booking_handoff', JSON.stringify({
              timestamp: Date.now(),
              channel: 'whatsapp',
              clubName: match.club?.club_name || null,
              matchId: match.id,
              groupId: match.group_id || null,
              playerIds: [match.team1_player1, match.team1_player2, match.team2_player1, match.team2_player2].filter(Boolean),
              matchDate: match.match_date,
              rosterLabel: buildRosterLabel(
                match.team1_player1_profile?.full_name,
                match.team1_player2_profile?.full_name,
                match.team2_player1_profile?.full_name,
                match.team2_player2_profile?.full_name
              ),
            }));
          };

          return (
            <div className="space-y-1.5">
              {/* Inline duration selector */}
              <div className="flex gap-1">
                {[60, 90, 120, 180].map((min) => (
                  <button
                    key={min}
                    type="button"
                    onClick={() => setCourtDuration(min)}
                    className={`flex-1 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                      courtDuration === min
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    {t('matches.whatsapp.minutes', { count: min })}
                  </button>
                ))}
              </div>

              {/* Side-by-side booking pills */}
              <div className="flex gap-1.5">
                {hasWhatsApp && waUrl && (
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleWhatsAppClick}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-primary/25 bg-primary/5 hover:bg-primary/10 transition-colors text-xs font-medium text-foreground"
                  >
                    <MessageCircle size={13} className="text-primary" />
                    <span>WhatsApp</span>
                  </a>
                )}
                {hasPlaytomic && (
                  <PlaytomicBookingButton
                    playtomicTenantId={match.club.playtomic_tenant_id!}
                    slug={match.club.slug!}
                    date={format(matchDate, 'yyyy-MM-dd')}
                    time={format(matchDate, 'HH:mm')}
                    durationMinutes={courtDuration}
                    clubName={match.club.club_name}
                    matchId={match.id}
                    groupId={match.group_id}
                    playerIds={[match.team1_player1, match.team1_player2, match.team2_player1, match.team2_player2]}
                    matchDate={match.match_date}
                    rosterLabel={buildRosterLabel(
                      match.team1_player1_profile?.full_name,
                      match.team1_player2_profile?.full_name,
                      match.team2_player1_profile?.full_name,
                      match.team2_player2_profile?.full_name
                    )}
                    size="pill"
                  />
                )}
              </div>
            </div>
          );
        })()}

        {/* Actions */}
        <div className="flex gap-2">
          {canCaptureScores ? (
            <Button 
              onClick={handleCaptureScores}
              className="flex-1 gap-2 h-9"
              size="sm"
            >
              <Trophy className="h-3.5 w-3.5" />
              Capture Scores
            </Button>
          ) : showCalendarButton && (
            <Button 
              onClick={handleAddToCalendar}
              className="flex-1 gap-2 h-9"
              variant="outline"
              size="sm"
            >
              <CalendarPlus className="h-3.5 w-3.5" />
              {getCalendarButtonText(calendarPreference)}
            </Button>
          )}

          {user && match.published_by === user.id && !canCaptureScores && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Scheduled Match</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this match? All players will be notified. This cannot be undone.
                    {match.challenge_match_id && (
                      <span className="block mt-2 font-medium text-destructive">
                        ⚠️ This match is linked to a challenge. Deleting it will automatically cancel the challenge and notify all 4 players.
                      </span>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteMatch}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    </Card>
    </div>

      {/* Partnership History Dialog */}
      <PartnershipHistoryDialog
        open={partnershipDialogOpen}
        onOpenChange={setPartnershipDialogOpen}
        partnerId={selectedPartnerId}
      />

      <ReplacePlayerDialog
        open={replaceSlot !== null}
        onOpenChange={(o) => { if (!o) setReplaceSlot(null); }}
        match={match}
        slot={replaceSlot}
      />
    </>
  );
};
